import { Link } from 'react-router-dom';
import { useBrowserSearch } from '@/hooks/useBrowserSearch';
import { ArrowRight } from 'lucide-react';
import { trackLandingDoorClicked } from '@/lib/analytics';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { useTranslation } from '@/i18n/i18n';

/**
 * Audience router strip (apps/landing/DESIGN.md §Named patterns — Door band).
 * Quiet wayfinding under the hero, NOT a competing hero: left-aligned, no
 * icons-in-circles, JDG door = yellow fill, others quiet border. Tap targets
 * exceed 44px; stacked full-width on mobile.
 */
export function DoorBand() {
  const signupUrl = useSignupUrl({ entityType: 'jdg', source: 'door_band' });
  const sectionRef = useLandingSectionTracking('door_band');
  const { t } = useTranslation();
  const localizedPath = useLocalizedPath();
  const search = useBrowserSearch() ?? '';
  const localized = (path: string) => ({
    pathname: localizedPath(path),
    search,
  });

  const doorLinkClasses =
    'group flex min-h-[72px] items-center justify-between gap-3 rounded-2xl px-5 py-4 transition-colors';

  return (
    <nav
      ref={sectionRef}
      aria-label={t('doors.ariaLabel')}
      className="px-6 md:px-8 py-8 border-b border-border bg-background"
    >
      <div className="max-w-6xl mx-auto">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">
          {t('doors.kicker')}
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          {/* JDG — direct self-serve signup with acquisition attribution. */}
          <a
            href={signupUrl}
            onClick={() => trackLandingDoorClicked('jdg')}
            className={`${doorLinkClasses} bg-primary text-primary-foreground hover:bg-primary/90`}
          >
            <span className="text-left">
              <span className="block font-bold">{t('doors.jdg.title')}</span>
              <span className="block text-sm opacity-80">{t('doors.jdg.description')}</span>
            </span>
            <ArrowRight
              className="size-5 shrink-0 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </a>
          <Link
            to={localized('/biura')}
            onClick={() => trackLandingDoorClicked('biura')}
            className={`${doorLinkClasses} border border-border bg-background hover:bg-muted/60`}
          >
            <span className="text-left">
              <span className="block font-bold">{t('doors.biura.title')}</span>
              <span className="block text-sm text-muted-foreground">
                {t('doors.biura.description')}
              </span>
            </span>
            <ArrowRight
              className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
          <Link
            to={localized('/wdrozenia')}
            onClick={() => trackLandingDoorClicked('wdrozenia')}
            className={`${doorLinkClasses} border border-border bg-background hover:bg-muted/60`}
          >
            <span className="text-left">
              <span className="block font-bold">{t('doors.wdrozenia.title')}</span>
              <span className="block text-sm text-muted-foreground">
                {t('doors.wdrozenia.description')}
              </span>
            </span>
            <ArrowRight
              className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
        </div>
      </div>
    </nav>
  );
}
